import { Form } from 'antd';
import { useCallback, useContext, useMemo, useState } from 'react';

import { type ModelTag, useUpdateTagMutation } from '@app/api/Tags';
import { SUCCESS } from '@app/constants/alerts';
import {
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { HeaderContext, NotificationContext } from '@app/providers';
import type { NNotificationProvider } from '@app/providers/NotificationProvider/NotificationProvider.types';
import { handleCatchError } from '@app/services';
import { useLazyGetListTagsQuery } from '@app/services/rtkQuery/Tags/api';
import type { NTags } from '@app/types';

interface ColorValue {
  metaColor: {
    toHexString: () => string;
  };
}

interface FormValues {
  Name: string;
  Color: string | ColorValue | null;
  Description: string | null;
  IsDeleted: boolean;
}

export const useTags = () => {
  const { activeBranchId, userId, database } = useUserPrint();
  const [tags, setTags] = useState<NTags.Tag[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState('fa-solid fa-tag');
  const [isLoading, setLoading] = useState(false);
  const [currentTagId, setCurrentTagId] = useState(0);
  const [isCurrentTagDeleted, setIsCurrentTagDeleted] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const {
    userAccessRights: { Tags, Clients },
  } = useNewUserAccessRights();

  const hasUserRightToEditTags = useMemo(
    () => Clients?.IsTagsChange,
    [Clients]
  );
  const IsOn = useMemo(() => Tags?.IsOn, [Tags]);

  const [form] = Form.useForm();

  const { showNotification } = useContext(NotificationContext);
  const { setHeader } = useContext(HeaderContext);

  const { isMobile } = useWindowSize();

  const [getAllTags] = useLazyGetListTagsQuery();
  const [updateTag] = useUpdateTagMutation();

  const getTagsHandler = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllTags({
        ActiveBranchId: activeBranchId,
      }).unwrap();

      setTags(result);
    } catch (error) {
      showNotification(
        (error as Error).message,
        (error as Error).cause as NNotificationProvider.NotifyType
      );
    } finally {
      setLoading(false);
    }
  }, [getAllTags, activeBranchId, showNotification]);

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
    setCurrentTagId(0);
    setIsCurrentTagDeleted(false);
    form.resetFields();
    setSelectedIcon('fa-solid fa-tag');
  }, [form]);

  const handleEdit = useCallback(
    (id: number) => {
      setIsModalOpen(true);
      const currentTag = tags.find((tag) => tag.Id === id);
      if (currentTag) {
        setCurrentTagId(currentTag.Id);
        setIsCurrentTagDeleted(currentTag.IsDeleted);
        setSelectedIcon(currentTag.IconId);
        form.setFieldsValue({
          Name: currentTag.Name,
          Color: currentTag.Color,
          Description: currentTag.Description,
          IsDeleted: currentTag.IsDeleted,
        });
      }
    },
    [tags, form]
  );

  const submitMainForm = useCallback(async () => {
    const values: FormValues = form.getFieldsValue();
    const tagModel: ModelTag = {
      Id: currentTagId ? currentTagId : 0,
      Name: values.Name,
      Color:
        typeof values.Color === 'string'
          ? values.Color
          : values.Color?.metaColor
          ? values.Color.metaColor.toHexString()
          : '#1677FF',
      IconId: selectedIcon,
      Description: values.Description,
      IsDeleted: currentTagId ? values.IsDeleted : false,
      IsSelected: false,
      IsSystem: false,
      SystemId: null,
    };

    try {
      await updateTag({
        ActiveBranchId: activeBranchId,
        data: tagModel,
        logs: { isLogged: true, userId, database },
      }).unwrap();
      setIsModalOpen(false);
      showNotification('', SUCCESS);
      setCurrentTagId(0);
      setIsCurrentTagDeleted(false);
      setSelectedIcon('fa-solid fa-tag');
      form.resetFields();
      await getTagsHandler();
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    form,
    currentTagId,
    selectedIcon,
    updateTag,
    activeBranchId,
    userId,
    database,
    showNotification,
    getTagsHandler,
  ]);

  const handleChange = useCallback((page: number, pageSize: number) => {
    setPageNumber(page);
    setRowsPerPage(pageSize);
  }, []);

  return {
    tags,
    handleEdit,
    isLoading,
    isModalOpen,
    handleCancel,
    selectedIcon,
    form,
    setSelectedIcon,
    submitMainForm,
    currentTagId,
    hasUserRightToEditTags,
    isMobile,
    handleChange,
    pageNumber,
    rowsPerPage,
    isCurrentTagDeleted,
    IsOn,
    getTagsHandler,
    setHeader,
    setIsModalOpen,
  };
};
